import { createTheme } from '@mui/material/styles';
import { colors, fonts, radii } from './tokens';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.white, contrastText: colors.black },
    secondary: { main: colors.textMuted },
    background: { default: colors.bg, paper: colors.surface },
    text: { primary: colors.text, secondary: colors.textMuted },
    divider: colors.border,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.heading, fontWeight: 700, lineHeight: 1.08, letterSpacing: '-0.02em' },
    h2: { fontFamily: fonts.heading, fontWeight: 700, lineHeight: 1.12, letterSpacing: '-0.015em' },
    h3: { fontFamily: fonts.heading, fontWeight: 600, lineHeight: 1.2 },
    h4: { fontFamily: fonts.heading, fontWeight: 600, lineHeight: 1.25 },
    h5: { fontFamily: fonts.heading, fontWeight: 600, lineHeight: 1.3 },
    h6: { fontFamily: fonts.heading, fontWeight: 600, lineHeight: 1.35 },
    subtitle1: { fontFamily: fonts.body, fontWeight: 500 },
    subtitle2: { fontFamily: fonts.body, fontWeight: 600 },
    body1: { fontFamily: fonts.body, lineHeight: 1.7 },
    body2: { fontFamily: fonts.body, lineHeight: 1.65 },
    button: { fontFamily: fonts.body, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    overline: {
      fontFamily: fonts.body,
      fontWeight: 600,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      fontSize: '0.72rem',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: 'smooth' },
        body: {
          backgroundColor: colors.bg,
          color: colors.text,
          WebkitFontSmoothing: 'antialiased',
        },
        '::selection': { backgroundColor: colors.white, color: colors.black },
        ':focus-visible': {
          outline: `2px solid ${colors.white}`,
          outlineOffset: 3,
          borderRadius: 4,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          paddingInline: 22,
          paddingBlock: 11,
          transition: 'background-color .2s ease, color .2s ease, border-color .2s ease, transform .2s ease',
        },
        sizeLarge: { paddingInline: 28, paddingBlock: 14, fontSize: '1rem' },
        sizeSmall: { paddingInline: 16, paddingBlock: 8, fontSize: '0.875rem' },
        containedPrimary: {
          backgroundColor: colors.white,
          color: colors.black,
          '&:hover': { backgroundColor: '#e6e6e6' },
          '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.28)', color: 'rgba(0,0,0,0.6)' },
        },
        outlinedPrimary: {
          borderColor: colors.borderStrong,
          color: colors.white,
          '&:hover': { borderColor: colors.white, backgroundColor: 'rgba(255,255,255,0.08)' },
        },
        textPrimary: {
          color: colors.white,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { color: colors.text },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: radii.lg },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radii.lg,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: radii.pill },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.04)',
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.white, borderWidth: 1.5 },
        },
        input: { color: colors.text },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.textMuted,
          '&.Mui-focused': { color: colors.white },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { color: colors.textSubtle } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          marginTop: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.12)' },
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: colors.white } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: colors.border } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: colors.surfaceHover, border: `1px solid ${colors.border}` },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radii.md, fontWeight: 500 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: colors.bg, border: `1px solid ${colors.border}` },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: colors.bg, backgroundImage: 'none' },
      },
    },
  },
});

export default theme;
